// Entry module for the client-side editor surfaces, built by Vite to
// public/js/editor/editor.js and loaded same-origin (passes require-corp).
//
// One bundle, dispatched on body[data-surface] (clientside-teacher-review, task 1.5):
//   editor      the default in-app / embed editor (lib/views/embed/pgzero-clientside-*)
//   review      the teacher review surface       (lib/views/embed/review.html)
//   assignment  the student assignment editor     (lib/views/embed/assignment.html)
//   viewonly    the read-only view-only surface   (lib/views/embed/viewonly.html)
//   tweak       the isolated tweak activity        (lib/views/embed/tweak.html)
//
// editor-core + Ace load once here; each surface only differs in its editor-core opts
// (readOnly / fileControls) and the per-surface chrome module it delegates to. The
// default editor surface (no data-surface, or "editor") is unchanged.
//
// Responsibilities (kept thin — reusable logic lives in editor-core / editor-api):
//   - isolation fallback: if the page is not cross-origin-isolated, show the
//     unsupported banner (there is no server-side editor to fall back to);
//   - construct editor-core (Ace + /runner/ iframe + Run/Stop + console) with the
//     surface's opts, load the server-rendered payload, then delegate to the chrome.

import { createEditorCore } from './editor-core.js';
import * as editorApi from './editor-api.js';
import { wire as wireEditor } from './editor-chrome.js';
import { wire as wireReview } from './review-chrome.js';
import {
  wire as wireAssignment, flushPendingSave,
  // one-tab-topbar: the activity top bar's Back flushes through these (aliased so the existing
  // `wireVersions` argument above stays exactly `flushPendingSave`).
  flushPendingSave as flushAssignmentSave, hasPendingSave as assignmentHasPendingSave,
  whenSubmitSettled as whenAssignmentSubmitSettled
} from './assignment-chrome.js';
import { wire as wireViewonly } from './viewonly-chrome.js';
import {
  wire as wireChallenge,
  flushPendingSave as flushChallengeSave, hasPendingSave as challengeHasPendingSave
} from './challenge-chrome.js';
import { wire as wireChallengeFeedback } from './challenge-feedback-chrome.js';
import { wire as wireTweak } from './tweak-chrome.js';
import { wire as wirePredict } from './predict-chrome.js';
import { wire as wireDebug } from './debug-chrome.js';
import { wire as wireCompletion } from './completion-chrome.js';
import { wire as wireTraceCapture } from './trace-capture-chrome.js';
import {
  wire as wireGuidedMake,
  // one-tab-same-tab-launches: the activity top bar's Back flushes the guided-make Draft.
  flushPendingSave as flushGuidedMakeSave, hasPendingSave as guidedMakeHasPendingSave
} from './guided-make-chrome.js';
import {
  wire as wireFindTheBug,
  // one-tab-same-tab-launches: the activity top bar's Back flushes the find-the-bug Draft.
  flushPendingSave as flushFindTheBugSave, hasPendingSave as findTheBugHasPendingSave
} from './find-the-bug-chrome.js';
import { wire as wireCompare } from './compare-chrome.js';
// canvas-play-button: makes the decorative pre-run stage overlay clickable. Wired
// uniformly (NOT dispatched by data-surface) — static import, not the dynamic
// import() precedent below, because that races the one-shot 'ready' emit.
import { wire as wireStagePrerun } from './stage-prerun.js';
// editor-save-shortcut-and-console-resize: two more cross-surface modules wired uniformly
// (NOT via the SURFACES map). The Ctrl-S listener and the stored console height wire
// EAGERLY below — above the isolation early-return and outside loadProject().then() — so
// the browser's Save Page dialog can never appear and the console never snaps from the
// default height to the stored one. Only the divider's interactive wiring stays late.
import { wireSaveShortcut } from './editor-shortcuts.js';
import { applyStoredConsoleHeight, wireConsoleResize } from './console-resize.js';
// read-aloud-instructions: the "Read aloud" control on the instruction prose. A third
// cross-surface module wired uniformly (NOT via the SURFACES map) and EAGERLY, for two
// reasons: the instruction HTML is server-rendered and the preference arrives as <body> data
// attributes, so it needs no project payload; and late wiring would silently skip
// `.tweak-intro` and `.instructions-pane` (neither is in the `body.no-isolation` hide list
// that makes late wiring harmless for the banner) and would skip EVERY surface whenever
// loadProject rejects. It never touches editor-core, so nothing forces it late.
import { wireReadAloud } from './read-aloud-chrome.js';
// time-on-task: the FOURTH cross-surface module wired uniformly (NOT via the SURFACES map) and
// EAGERLY, for the same reason as read-aloud above: its inputs are the server-rendered <body>
// attributes and the surface's own `#*-config` JSON, so it needs neither the project payload
// nor getEditor(). Late wiring would drop recording entirely whenever loadProject rejects, and
// an isolation failure must not silently stop a student's time being counted either. It
// allow-lists the seven student activity surfaces itself — `review` and `challengeFeedback`
// also carry a material id, and recording a teacher's marking time would be a real defect.
import { wireTimeOnTask } from './time-on-task-chrome.js';
// activity-intro-tours (Trello dxJDDAx5): the FIFTH cross-surface module wired uniformly (NOT
// via the SURFACES map) and EAGERLY, for the same reason as read-aloud and time-on-task above:
// its inputs are the server-rendered <body> attributes and the surface's own `#*-config` JSON,
// so it needs neither the project payload nor getEditor(). Late wiring would drop the "How this
// works" control whenever loadProject rejects — and the control must still be there when the
// payload fails to load, which is exactly when a stuck student needs it. It carries its own
// `data-surface` allow-list (predict only today), because wired uniformly it would otherwise run
// on the teacher surfaces `review` and `challengeFeedback` too.
import { wireActivityTour } from './activity-tour-chrome.js';
// activity-retry-and-close-on-success (Trello 2eajTgm0): the SIXTH cross-surface module wired
// uniformly (NOT via the SURFACES map) — the "shall I take you back?" countdown offered on the
// five auto-marked surfaces when a student succeeds. Unlike read-aloud and time-on-task it
// wires LATE, inside loadProject().then(): it needs `core.getRunnerFrame()` so it can also
// watch for interaction INSIDE the runner frame, which is where a student works after passing
// a challenge or a guided build and whose events never reach this document. Nothing arms on
// page load, so late wiring costs nothing — the surfaces call into it at their success points.
import { wireClosePrompt } from './close-prompt.js';
// class-block-paste (Trello l3TPxPs3): the SEVENTH cross-surface module wired uniformly (NOT via
// the SURFACES map) and EAGERLY, for the same reason as read-aloud, time-on-task and the tour: its
// inputs are the server-rendered <body> attributes and the surface's own `#*-config` JSON, so it
// needs neither the project payload nor getEditor(). It refuses paste/cut/text-drop at DOCUMENT
// capture phase, which is what lets it wire with no readiness gate — the graded answer fields on
// predict and completion are rendered by their own chrome AFTER this runs. It carries its own
// `data-surface` allow-list (the seven student activity surfaces), because wired uniformly it
// would otherwise run on the teacher surfaces `review` and `challengeFeedback` and on the default
// `editor` surface, none of which belongs to a class whose setting could be read.
import { wireNoPaste } from './no-paste-chrome.js';
// plan-first-activity (Trello 5mlpF1aX): the plan block beside the editor. The NINTH sibling
// chrome module and NOT a uniformly-wired one — it ALLOW-LISTS `guided-make | assignment` itself
// and is a no-op on every other surface and on a host with no `#plan-config`. Wired EAGERLY (its
// inputs are server-rendered; on a LOCKED page the plan panel is the whole activity and must not
// depend on loadProject). `isPlanLocked` decides the locked-mode no-ops below.
import { wirePlanChrome, isPlanLocked } from './plan-chrome.js';
// activity-test-table (Trello 2P8y4y9S): the test table beside the editor. The TENTH sibling
// chrome module and NOT a uniformly-wired one — it ALLOW-LISTS `assignment | challenge |
// guided-make` itself and is a no-op anywhere else and on a host with no `#test-table-config`.
// Wired LATE (below, after surface.wire): it needs `core` (the student's own code runs in the
// visible runner) and the bundled Ace (its hidden runner is a second editor-core). Never wired on a
// LOCKED plan page — the early return below skips it, which is the plan rail's own treatment.
import { wireTestTable } from './test-table-chrome.js';
// guided-make variable readout (Trello lFu60Fgm): "Show my variables". An ELEVENTH sibling chrome
// module and NOT a uniformly-wired one — it ALLOW-LISTS `guided-make` itself and is a no-op on
// every other surface. Wired LATE (below, after the test table): it needs `core` (the live main.py)
// and the bundled Ace (its off-screen runner is a second editor-core, hidden-runner.js). It runs
// nothing until the student presses its button, and never on a LOCKED plan page.
import { wireVariableTrace } from './variable-trace-chrome.js';
// challenge-panel-resize (Trello LiwLkRvL + JBfO8Ilp): the challenge surface's "What to add"
// drawer (width) and instructions banner (height) are resizable. An ALLOW-LISTED sibling like
// plan-chrome (a no-op unless data-surface="challenge"), split like console-resize: the stored
// sizes apply EAGERLY so nothing snaps on load; the separators wire LATE (getEditor().resize()).
import { applyStoredChallengePanels, wireChallengePanelResize } from './challenge-panel-resize.js';
// friendlier-challenges (Trello d7cn4sN9): the challenge's insert-at-cursor code chips. Wired LATE
// for `challenge` only (needs core.getEditor()); challenge-chrome renders the chips through the
// same module's chipifyHtml / buildChip.
import { wireInsertChips } from './challenge-insert-chrome.js';
// student-versions (Trello ch3IfCAK): "Save version" / "Versions" on the student's own assignment
// working copy. The THIRTEENTH sibling chrome module and NOT a uniformly-wired one — it ALLOW-LISTS
// `assignment` itself and is a no-op without `#versions-config`. Wired LATE (below, after the test
// table and the variable readout): it needs `core` for the live files, and its capture-phase Submit
// nudge must sit behind the test table's own Submit guard. Never wired on a LOCKED plan page.
import { wireVersions } from './versions-chrome.js';
// guided-make-uploads (Trello KvfVxVhb): the Images & sounds (Assets) panel on a guided make whose
// teacher switched "Let students upload pictures and sounds" on. The FOURTEENTH sibling chrome module
// and NOT a uniformly-wired one — it ALLOW-LISTS `guided-make` itself and is a no-op without
// `allowUploads: true` in `#guided-make-config` and the server-rendered `#runner-assets` /
// `#assets-modal`. Wired LATE (below, after the versions chrome): the shared `wireAssets` it calls
// needs `core` and the load `payload`. Never wired on a LOCKED plan page.
import { wireGuidedMakeUploads } from './guided-make-uploads-chrome.js';
// activity-download (Trello DNivkjIh): the "Download" button on the student's own working copy. The
// FIFTEENTH sibling chrome module and NOT a uniformly-wired one — it ALLOW-LISTS `assignment |
// challenge | guided-make` itself and is a no-op without the server-rendered `#runner-download`.
// Wired LATE (below, after the uploads chrome): it needs `core` (the live buffer) and the load
// `payload` (asset base URL, asset list, mode). Never wired on a LOCKED plan page.
import { wireDownload } from './download-chrome.js';
// one-tab-topbar (Trello 3h9FqNWJ): the activity top bar's Back. The SIXTEENTH sibling chrome
// module and NOT a uniformly-wired one — it ALLOW-LISTS `assignment | challenge | viewonly` and
// (one-tab-same-tab-launches) the seven tutor surfaces itself, and is a no-op without the
// server-rendered `#surface-topbar-back`. Wired EAGERLY (below, beside
// plan-chrome, above the isolation early return): its inputs are server markup and <body>
// attributes, and Back must work even when the page cannot isolate or loadProject rejects. The
// surface's save functions are PASSED IN (late-bound — safe before `surface.wire` runs); it
// imports only close-prompt.js.
import { wireSurfaceTopbar } from './surface-topbar-chrome.js';

// Modern Ace (1.43.x), bundled into editor.js's OWN Vite output and passed as
// opts.ace. Static imports of the mode and theme keep Ace away from its runtime
// net-loader, so nothing is fetched cross-origin at runtime (COEP-safe).
import ace from 'ace-builds/src-noconflict/ace';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/mode-text';   // non-.py files use ace/mode/text (editor-core)
import 'ace-builds/src-noconflict/theme-xcode';

(function () {
  'use strict';

  // The server-staged language mode ('' => pgzero, 'pygame', 'python').
  var clientsideMode = document.body.getAttribute('data-clientside-mode') || '';

  // Ctrl-S / Cmd-S is swallowed on EVERY path, including the two below that return before
  // any other wiring and the seconds before the payload resolves — the browser's Save Page
  // dialog must never appear. Saving itself only happens once a surface has registered a
  // handler; before that (and on surfaces that never register) the keystroke is swallowed
  // and nothing else happens. Both calls are null-safe and never throw.
  // plan-first-activity: a LOCKED plan page (a hard-gated plan block and no submitted plan) has
  // no source and no activity yet — only the plan panel. While locked: the editor mounts
  // READ-ONLY, `surface.wire` is NOT called (no autosave, no save-registry handler, no step
  // chrome), `wireClosePrompt` is skipped, and the eager intro tour is SKIPPED with no seen-key
  // write (the guided-make tour belongs to the unlocked activity). A submit reloads the page.
  var planLocked = isPlanLocked(document);

  wireSaveShortcut({ doc: document, frame: document.getElementById('runner-frame') });
  applyStoredConsoleHeight(document);
  applyStoredChallengePanels(document);
  wireReadAloud(document);
  wireTimeOnTask(document);
  if (!planLocked) { wireActivityTour(document); }
  wireNoPaste(document);
  wirePlanChrome(document);
  // one-tab-topbar: the surface is read HERE, at the eager point, to choose the save functions
  // Back flushes through (viewonly: none — nothing to save). Each is late-bound: before
  // `surface.wire` runs it reports nothing pending, and an unwired flush is never called.
  var topbarSurface = document.body.getAttribute('data-surface') || '';
  wireSurfaceTopbar(document, topbarSurface === 'assignment' ? {
    surface: topbarSurface,
    hasPendingSave: function () { return assignmentHasPendingSave(); },
    flushPendingSave: function () { return flushAssignmentSave(); },
    whenSubmitSettled: function () { return whenAssignmentSubmitSettled(); }
  } : topbarSurface === 'challenge' ? {
    surface: topbarSurface,
    hasPendingSave: function () { return challengeHasPendingSave(); },
    flushPendingSave: function () { return flushChallengeSave(); }
  } : topbarSurface === 'guided-make' ? {
    surface: topbarSurface,
    hasPendingSave: function () { return guidedMakeHasPendingSave(); },
    flushPendingSave: function () { return flushGuidedMakeSave(); }
  } : topbarSurface === 'find-the-bug' ? {
    surface: topbarSurface,
    hasPendingSave: function () { return findTheBugHasPendingSave(); },
    flushPendingSave: function () { return flushFindTheBugSave(); }
  } : { surface: topbarSurface });

  // Isolation fallback. crossOriginIsolated is false when the browser cannot isolate
  // the page OR when the operator kill-switch features.pgzeroClientside is off (the
  // controller then does not flag the response). Either way the in-browser runner
  // cannot work and there is NO server-side editor to fall back to, so show the
  // unsupported banner directly for every surface.
  if (window.crossOriginIsolated === false) {
    document.body.classList.add('no-isolation');
    return;
  }

  var frame = document.getElementById('runner-frame');
  var mount = document.getElementById('editor');
  if (!frame || !mount) { return; }

  var trinketId = document.body.getAttribute('data-project-id') || '';
  // Owner signal (mirrors the template's isOwner gating used for Save): the body
  // carries data-is-owner only for owners, so file management + the Assets panel are
  // offered exactly when Save is.
  var isOwner = document.body.getAttribute('data-is-owner') === 'true';

  // Per-surface editor-core options + chrome wiring. A surface absent from the map
  // (or no data-surface attribute) falls back to the default editor surface so the
  // existing editor path is unchanged.
  var SURFACES = {
    editor:            { readOnly: false, fileControls: isOwner, wire: wireEditor },
    review:            { readOnly: false, fileControls: false,   wire: wireReview },
    assignment:        { readOnly: false, fileControls: isOwner, wire: wireAssignment },
    viewonly:          { readOnly: true,  fileControls: false,   wire: wireViewonly },
    // code-challenge-activity (task 4/6): student Check surface + teacher
    // challenge-review surface — mirror assignment/review with a narrowed
    // lifecycle (no submit/date UI, no submission-state pill).
    challenge:         { readOnly: false, fileControls: isOwner, wire: wireChallenge },
    challengeFeedback: { readOnly: false, fileControls: false,   wire: wireChallengeFeedback },
    // tweak-activity ([4.1]): the isolated tweak activity. The student never edits raw
    // code (readOnly + no file controls); tweak-chrome renders a control panel beside
    // the game and re-runs it client-side via editor-core load()/run() on each change.
    tweak:             { readOnly: true,  fileControls: false,   wire: wireTweak },
    // predict-activity ([2.3]): the isolated predict activity. The student never edits
    // raw code (readOnly + no file controls); predict-chrome renders a prediction panel
    // beside the read-only code, gates Run until the prediction is committed, and reveals
    // a client-side compare of prediction vs actual output.
    predict:           { readOnly: true,  fileControls: false,   wire: wirePredict },
    // debug-activity ([5.1]): the isolated debug activity. The student EDITS the buggy
    // code (readOnly:false, no file controls) and the shared Run stays visible; a
    // dedicated Check runs the live buffer and grades its stdout against the shipped
    // golden client-side (challenge-grading.js compareExpectedOutput).
    debug:             { readOnly: false, fileControls: false,   wire: wireDebug },
    // completion-activity ([3.3]): the isolated fill-in-the-blanks activity. The student
    // never edits raw code (readOnly + no file controls); completion-chrome renders the
    // BLANKED code line-by-line with a single-line input per blank, then a dedicated Check
    // reconstructs + runs the completed source and grades its stdout against the shipped
    // golden client-side (challenge-grading.js compareExpectedOutput).
    completion:        { readOnly: true,  fileControls: false,   wire: wireCompletion },
    // trace-table-capture: the isolated teacher-only CAPTURE surface. Read-only source
    // (no editing, no file controls); trace-capture-chrome drives ONE instrumented
    // per-line trace-capture pass and hands the discovered variables + trace back to the
    // course-editor dialog over a BroadcastChannel. Authoring convenience — no grading.
    'trace-capture':   { readOnly: true,  fileControls: false,   wire: wireTraceCapture },
    // guided-make-activity (card sTMSPmGQ): the isolated "make it step by step" activity.
    // The student EDITS their OWN working Snippet (readOnly:false, no file controls) with the
    // shared Run visible; guided-make-chrome renders the ordered step chrome (prompt/hint,
    // advisory Check ✓/✗ rail, Back/Next, a final-step model reveal) and autosaves to Draft.
    // Completion is a MaterialCompletion tick: a run STARTED on the final step reaching
    // `running` or a terminal state (guided-make-finish-gate, Trello YXAF0iky).
    'guided-make':     { readOnly: false, fileControls: false,   wire: wireGuidedMake },
    // find-the-bug-activity (Trello C902VeMO): the test-find-fix-explain activity. readOnly is
    // FALSE here because the student fixes the code once the bug is found; the chrome itself
    // locks the Ace buffer (`setReadOnly`) until its tests find the bug, then unlocks it.
    'find-the-bug':    { readOnly: false, fileControls: false,   wire: wireFindTheBug },
    // compare-activity (Trello m98B2mq7): "Compare algorithms". The visible editor holds only a
    // synthesized one-line boot program and is hidden by the template (readOnly); the versions
    // run in the HIDDEN runner, driven by compare-chrome through hidden-runner.js's public API.
    compare:           { readOnly: true,  fileControls: false,   wire: wireCompare }
  };
  var surfaceName = document.body.getAttribute('data-surface') || 'editor';
  var surface = SURFACES[surfaceName] || SURFACES.editor;

  editorApi.loadProject(trinketId).then(function (payload) {
    var core = createEditorCore({
      ace: ace,                                         // bundled modern Ace (1.43.x)
      frame: frame,
      mount: mount,
      // Prefer the server-staged payload.mode; fall back to the body attribute.
      mode: payload.mode || clientsideMode,
      // view-only surfaces (task 5); plan-first-activity: a LOCKED plan page is read-only too.
      readOnly: planLocked ? true : surface.readOnly,
      consoleEl: document.getElementById('runner-console'),
      statusEl: document.getElementById('runner-status'),
      runBtn: document.getElementById('runner-run'),
      stopBtn: document.getElementById('runner-stop'),
      tabsEl: document.getElementById('editor-tabs'),   // multi-file tab strip (absent => no tabs)
      fileControls: surface.fileControls,               // owner-only add/rename/delete
      a11yEl: document.getElementById('runner-a11y'),   // accessibility toggle (absent => no a11y)
      stageEl: document.querySelector('.runner-stage')  // canvas stage; hidden for python (design D3)
    });

    // Load the full payload (source + files + assets) so the editor builds one
    // tab/buffer per file; single-file trinkets show no tab chrome.
    core.load(payload);

    // canvas-play-button: wire the pre-run stage control BEFORE surface.wire(), so
    // 'ready' listener order is deterministic. Null-safe — a no-op on any surface
    // (or template) with no #stage-prerun markup.
    wireStagePrerun(core);

    // editor-save-shortcut-and-console-resize: the divider's interactive behaviour needs
    // getEditor().resize(), so it waits for core — unlike the stored height, which was
    // already applied eagerly above. Null-safe on any template without #console-divider.
    wireConsoleResize(core);
    wireChallengePanelResize(core, document);

    // activity-retry-and-close-on-success: install the close-on-success prompt BEFORE
    // surface.wire(), so a chrome module that reaches its success point during wiring (a
    // hydrated completion, say) finds it ready. It arms nothing by itself.
    // plan-first-activity: on a LOCKED plan page the close prompt and the surface chrome are
    // NO-OPS — nothing may autosave (an empty buffer must never overwrite real work), register a
    // Ctrl-S handler, arm a countdown or render the steps. The stage control and the console
    // divider above still wire: they are layout, and write nothing.
    if (planLocked) {
      window.__editorCore = core;
      return;
    }

    wireClosePrompt(document, { core: core });

    // Delegate to the per-surface host chrome (Save/Share/… for editor; feedback for
    // review; autosave+Submit for assignment; Back for viewonly).
    if (surface.wire) {
      surface.wire({
        core: core,
        payload: payload,
        trinketId: trinketId,
        clientsideMode: clientsideMode,
        isOwner: isOwner,
        editorApi: editorApi,
        doc: document
      });
    }

    // friendlier-challenges: the challenge's insert-at-cursor code chips — ONE delegated listener
    // on <body>, which holds both the "What to add" drawer and the Check result modal. After the
    // surface chrome (which renders the chips). Never throws past here.
    if (surfaceName === 'challenge') {
      try { wireInsertChips({ core: core, root: document.body }); } catch (err) { console.error(err); }
    }

    // activity-test-table: after the surface chrome, so the assignment Submit button it gates has
    // already been wired. Never throws.
    wireTestTable({ core: core, ace: ace, doc: document });

    // guided-make variable readout: after the test table, so its section lands under the step's
    // check rail with the table still last. On-demand only; never throws.
    wireVariableTrace({ core: core, ace: ace, doc: document, mode: payload.mode || clientsideMode });

    // student-versions: last of the late siblings (see the import). Never throws past here.
    try {
      wireVersions({ core: core, doc: document, editorApi: editorApi, flushPendingSave: flushPendingSave });
    } catch (err) { console.error(err); }

    // guided-make-uploads: the guided-make Images & sounds panel (see the import). Never throws.
    try {
      wireGuidedMakeUploads({
        core: core, payload: payload, trinketId: trinketId, editorApi: editorApi, doc: document
      });
    } catch (err) { console.error(err); }

    // activity-download: the Download button (see the import). Never throws.
    try {
      wireDownload({ core: core, surface: surfaceName, payload: payload, doc: document });
    } catch (err) { console.error(err); }

    // code-challenge-activity (task 5.2): the "Record Answer Key" panel is layered
    // ON TOP of the default editor surface (not a separate data-surface entry), so
    // the teacher keeps normal Save/Share/Run while also recording. Inert unless
    // the template rendered the panel (owner + challengeRecord context resolved
    // server-side — see project.js getByShortCode).
    if (document.body.getAttribute('data-challenge-record') === 'true') {
      import('./challenge-record-chrome.js').then(function (mod) {
        mod.wire({
          core: core,
          payload: payload,
          trinketId: trinketId,
          clientsideMode: clientsideMode,
          editorApi: editorApi,
          doc: document
        });
      }).catch(function (err) { console.error(err); });
    }

    // Expose for the smoke harness / manual force-kill.
    window.__editorCore = core;
  }).catch(function (err) {
    var statusEl = document.getElementById('runner-status');
    if (statusEl) {
      statusEl.textContent = 'Could not load the editor.';
      statusEl.classList.add('runner-error');
    }
    console.error(err);
  });
})();
